//////////////////////////////////////////////////////////////////////////////////////   IMPORT
///////////////////////////////////////////////////////////   REACT
import React from 'react';
///////////////////////////////////////////////////////////   LIBS
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import captureVideoFrame from 'capture-video-frame';
import JSZip from 'jszip';
import JSZipUtils from 'jszip-utils';
import { saveAs } from 'file-saver';
///////////////////////////////////////////////////////////   METHODS
import { removeAccents } from '../../../assets/js/general-methods';
///////////////////////////////////////////////////////////   SETTINGS
import { _SETTINGS } from 'config/settings';
///////////////////////////////////////////////////////////   SCSS
import './photo-panel.styles.scss';
///////////////////////////////////////////////////////////   FONTAWESOME
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faAngleRight, fas } from '@fortawesome/free-solid-svg-icons';
import { faFileArchive, faCheckCircle, faExpand, faExclamationCircle, faSave, faCamera, faSpinner, faPortrait, faImage, faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faCaretLeft, faCaretRight } from '@fortawesome/free-solid-svg-icons';
//////////////////////////////////////////////////////////////////////////////////////    CLASS SETUP
class PhotoPanel extends React.Component<any, { [key: string]: any }> {
  private canvasA: React.RefObject<HTMLCanvasElement>;
  public leftRightPos: number = 0;
  constructor(props: any) {
    super(props);
    this.canvasA = React.createRef();
    this.state = {
      ...props,
      vw: null,
      vh: null,
      vx: this.props.vxRef,
      snapshotImgData: null,
      snapshotImgDataFull: null,
      snapTrgPos: 'fit',
      snapInputName: null,
      snapInputError: false,
      snapInputErrorMsg: null,
      zippingAllSnapshot: false,
      posRightLeft: 1
    };
  }
  ///////////////////////////////////////////////////////////   LIFECYCLE METHODS
  componentDidUpdate(prevProps: any) {
    if (this.props.vxRef !== prevProps.vxRef) {
      this.setState({ vx: this.props.vxRef });
    }
  }
  ///////////////////////////////////////////////////////////   CLASS METHODS
  /**
   * Handle creating snapshots
   */
  private handleCreateSnapshot = async (snapTrgPos: string | null) => {
    try {
      const { vx, posRightLeft } = this.state;
      if (vx) {
        vx.current.vp.current.player.pause();
        if (snapTrgPos === 'fitMin10') this.setState({ posRightLeft: posRightLeft - 0.1 >= 0 ? posRightLeft - 0.1 : 0 });
        if (snapTrgPos === 'fitPlus10') this.setState({ posRightLeft: 0.1 + posRightLeft <= 2 ? 0.1 + posRightLeft : 2 });
        this.setState(
          {
            vw: vx.current.vp.current.player.videoWidth(),
            vh: vx.current.vp.current.player.videoHeight(),
            snapTrgPos: snapTrgPos
          },
          async () => {
            // there is a slight delay, becasue we need to sync video frame first..
            let snapShot: any = await this.createSnapshotFromVideo();
            if (snapShot) {
              this.setState({ isSnapshotCreated: true });
              await this.createAllCanvases();
            } else {
              this.setState({ isSnapshotCreated: false });
            }
          }
        );
      }
    } catch (e) {
      console.log(e);
    }
  };

  /**
   * Handle creating snapshots
   */
  private createSnapshotFromVideo = () => {
    let { vx, vw, vh } = this.state;
    try {
      this.setState({ snapshotImgData: null, snapshotImgDataFull: null });
      const canvasSinglePhoto: HTMLCanvasElement | any = document.getElementById('singlePhotoCanvas')!,
        context = canvasSinglePhoto?.getContext('2d');
      let frame = captureVideoFrame(vx.current.vp.current.player.player_.children_[0], _SETTINGS.basic.imgFormat),
        image = new Image();
      this.setState({ snapshotImgData: frame.dataUri, snapshotImgDataFull: frame });
      image.src = frame.dataUri;
      context.imageSmoothingQuality = 'high';
      context.mozImageSmoothingEnabled = false;
      context.webkitImageSmoothingEnabled = false;
      context.msImageSmoothingEnabled = false;
      context.imageSmoothingEnabled = false;
      return (image.onload = () => {
        context.drawImage(image, 0, 0, vw, vh, 0, 0, vw, vh);
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   * Handle zipped snapshot
   */
  private handleZipSnapshot = () => {
    this.setState({ zippingAllSnapshot: true });
    setTimeout(() => {
      let { isSnapshotCreated } = this.state;
      if (isSnapshotCreated) {
        this.saveAllImagesToZip();
      }
    }, 1000);
  };

  /**
   * Handle save snapshot
   */
  private handleSaveSnapshot = () => {
    let { isSnapshotCreated, snapInputName } = this.state;
    if (isSnapshotCreated) {
      const canvasSinglePhoto: HTMLCanvasElement | any = document.getElementById('singlePhotoCanvas')!;
      canvasSinglePhoto.toBlob(function (blob: Blob) {
        saveAs(blob, `${snapInputName}.${_SETTINGS.basic.imgFormat}`);
      }, _SETTINGS.basic.imgMime);
    }
  };

  /**
   * Url to promise
   * @param url
   * @returns promise
   */
  private urlToPromise = (url: string) => {
    try {
      return new Promise(function (resolve, reject) {
        JSZipUtils.getBinaryContent(url, function (err: any, data: any) {
          if (err) {
            reject(err);
            throw new Error('In urlToPromise JSZipUtils failed!');
          } else {
            resolve(data);
          }
        });
      });
    } catch (e) {
      console.log(e);
    }
  };

  /**
   * Is canvas blank?
   * @param canvas
   * @returns boolean
   */
  private isCanvasBlank = (canvas: HTMLCanvasElement): boolean => {
    let blank = document.createElement('canvas');
    blank.width = canvas.width;
    blank.height = canvas.height;
    return canvas.toDataURL() === blank.toDataURL();
  };

  /**
   * Save all image to a zip
   */
  private saveAllImagesToZip = () => {
    let zip: JSZip = new JSZip();
    const { outputRes, snapInputName, snapTrgPos } = this.state;
    let { snapshotDesignBg } = this.state;
    if (snapTrgPos !== 'scale') {
      snapshotDesignBg = snapshotDesignBg.filter((obj: any) => {
        let n: string = obj.name;
        return n.toLocaleLowerCase() === 'mtva';
      });
    }
    snapshotDesignBg?.forEach((design: any, k: number) => {
      let imgFolder: JSZip | null = zip.folder(design.safeName);
      outputRes?.forEach((res: any, key: number) => {
        res.data.forEach((v: any, k: number) => {
          if (v.opt) {
            let imgIdName = `${design.safeName}_${v.label}`,
              canvasA = document.getElementById(`${res.name}_${design.safeName}_${v.label}`) as HTMLCanvasElement;
            if (!this.isCanvasBlank(canvasA)) {
              let dataUrl: string = canvasA.toDataURL(_SETTINGS.basic.imgMime),
                d: any = this.urlToPromise(dataUrl);
              imgFolder?.file(`${snapInputName}_${res.name}_${imgIdName}.${_SETTINGS.basic.imgFormat}`, d, {
                binary: true
              });
            }
          }
        });
      });
    });
    if (snapshotDesignBg && outputRes) {
      zip.generateAsync({ type: 'blob' }).then((content) => {
        saveAs(content, `${snapInputName}.zip`);
        setTimeout(() => {
          this.setState({ zippingAllSnapshot: false });
        }, 1000);
      });
    }
  };

  /**
   * Redraw images on canvas
   */
  private redrawImgOnCanvas = (): void => {
    let { snapshotReady, vw, vh } = this.state;
    const ctx: CanvasRenderingContext2D | null = this.canvasA.current ? this.canvasA.current.getContext('2d') : null,
      image = new Image();
    image.src = snapshotReady;
    image.onload = () => {
      ctx!.drawImage(image, 0, 0, vw, vh, 0, 0, vw, vh);
    };
  };

  /**
   *  Create all canvases and fills
   */
  private createAllCanvases = (): void => {
    try {
      const { outputRes, snapshotDesignBg, snapshotImgData, vw, vh, snapTrgPos, posRightLeft } = this.state;
      let snapshotDesignBgs = snapshotDesignBg ? snapshotDesignBg : this.props.outputRes.snapshotDesignBg;
      this.setState({ snapshotDesignBg: snapshotDesignBgs });
      snapshotDesignBgs?.forEach((design: any, k: number) => {
        outputRes?.forEach((res: any, key: number) => {
          res.data.forEach((v: any, k: number) => {
            if (v.opt) {
              const imgIdName: string = `${res.name}_${design.safeName}_${v.label}`,
                canvasImported: HTMLCanvasElement | any = document.getElementById(imgIdName)!,
                context: CanvasRenderingContext2D = canvasImported.getContext('2d'),
                image: HTMLImageElement = new Image(),
                bgImg: HTMLImageElement = new Image();
              let hRatio: number = v.width / vw,
                vRatio: number = v.height / vh,
                ratio: number = Math.min(hRatio, vRatio),
                centerShift_x: number = (v.width - vw * ratio) / 2,
                centerShift_y: number = (v.height - vh * ratio) / 2;
              bgImg.src = v.bg;

              switch (design.safeName) {
                case 'mtva':
                  bgImg.onload = () => {
                    if (snapTrgPos === 'scale') context.drawImage(bgImg, 0, 0, v.width, v.height);
                    image.src = snapshotImgData;
                  };
                  break;
                case 'fekete':
                  context.fillStyle = '#000';
                  context.fillRect(0, 0, v.width, v.height);
                  image.src = snapshotImgData;
                  break;
                case 'attetszo':
                  context.clearRect(0, 0, v.width, v.height);
                  context.fillStyle = 'rgba(0, 0, 0, 0)';
                  context.fillRect(0, 0, v.width, v.height);
                  image.src = snapshotImgData;
                  break;
                default:
                  context.clearRect(0, 0, v.width, v.height);
                  context.fillStyle = 'rgba(0, 0, 0, 0)';
                  context.fillRect(0, 0, v.width, v.height);
                  image.src = snapshotImgData;
                  break;
              }
              image.onload = () => {
                let scale = Math.max(v.width / vw, v.height / vh),
                  x = v.width / 2 - (vw / 2) * scale,
                  y = v.height / 2 - (vh / 2) * scale;

                switch (snapTrgPos) {
                  case 'fit':
                    this.setState({ posRightLeft: 1 });
                    context.drawImage(image, x, y, vw * scale, vh * scale);
                    break;
                  case 'fitPlus10':
                    context.drawImage(image, x * posRightLeft, y, vw * scale, vh * scale);
                    break;
                  case 'fitMin10':
                    context.drawImage(image, x * posRightLeft, y, vw * scale, vh * scale);
                    break;
                  case 'fitLeft50':
                    this.setState({ posRightLeft: 0.5 });
                    context.drawImage(image, x / 2, y, vw * scale, vh * scale);
                    break;
                  case 'fitLeft100':
                    this.setState({ posRightLeft: 0 });
                    context.drawImage(image, 0, y, vw * scale, vh * scale);
                    break;
                  case 'fitRight50':
                    this.setState({ posRightLeft: 1.5 });
                    context.drawImage(image, x * 1.5, y, vw * scale, vh * scale);
                    break;
                  case 'fitRight100':
                    this.setState({ posRightLeft: 2 });
                    context.drawImage(image, x * 2, y, vw * scale, vh * scale);
                    break;
                  case 'scale':
                    this.setState({ posRightLeft: 1 });
                    context.drawImage(image, 0, 0, vw, vh, centerShift_x, centerShift_y, vw * ratio, vh * ratio);
                    break;
                  default:
                    this.setState({ posRightLeft: 1 });
                    context.drawImage(image, x, y, vw * scale, vh * scale);
                    break;
                }
              };
            }
          });
        });
      });
    } catch (e) {
      console.log(e);
    }
  };
  /**
   * Set and validate snapshot name
   * Validating input file name while user is typing
   * @param e :input element
   */
  private setAndValidateSnapshotName = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    let err: string = '',
      imgName = removeAccents(value)
        .replace(/[^a-zA-Z0-9-_]/g, '')
        .toLowerCase();
    if (imgName.length < _SETTINGS.snap.minLength) err = _SETTINGS.err.snap.tooShort;
    if (imgName.length > _SETTINGS.snap.maxLength) err = _SETTINGS.err.snap.tooLong;
    this.setState({
      snapInputError: err || imgName.length === 0,
      snapInputErrorMsg: err,
      snapInputName: imgName
    });
  };
  ///////////////////////////////////////////////////////////   RENDER
  render() {
    const { snapshotDesignBg, outputRes, vw, vh } = this.state;
    const { snapInputName, snapInputError, snapInputErrorMsg, snapTrgPos, snapshotImgData, zippingAllSnapshot } = this.state;
    return (
      <>
        <Row>
          <Col>
            <canvas id='singlePhotoCanvas' width={vw} height={vh} ref={this.canvasA} />
          </Col>
        </Row>
        <Row>
          <Col>
            <Button variant='outline-primary' className='me-2 mb-1 func-btn' onClick={() => this.handleCreateSnapshot(null)}>
              <span className='sendFrameText '>
                <FontAwesomeIcon icon="camera" /> Kép
              </span>
            </Button>
            <Button disabled={!snapshotImgData || !snapInputName || snapInputError ? true : false} variant='outline-primary' className='me-2 mb-1 func-btn' onClick={() => this.handleSaveSnapshot()}>
              <span className='sendFrameText'>
                <FontAwesomeIcon icon="save" /> Ment
              </span>
            </Button>
            {snapshotDesignBg && outputRes ? (
              <Button disabled={!snapshotImgData || !snapInputName || snapInputError ? true : false} variant='outline-primary' className='me-2 mb-1 func-btn' onClick={() => this.handleZipSnapshot()}>
                <span className='sendFrameText'>
                  {!zippingAllSnapshot ? (
                    <>
                      <FontAwesomeIcon icon="file-archive" /> Csomagol
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon className='spinner-zipper' icon="spinner" /> Csomagolás folyamatban
                    </>
                  )}
                </span>
              </Button>
            ) : null}
          </Col>
        </Row>
        {snapshotDesignBg.length ? (
          <Row>
            <Col xs={12}>
              <span className='crop-input-title'>KÉP MOZGATÁSA:</span>
              <Button variant='outline-primary' className='me-2 mb-1 func-btn' onClick={() => this.handleCreateSnapshot('fitLeft100')}>
                <span className='sendFrameText' title='Teljesen balra igazítva'>
                  <FontAwesomeIcon icon="angle-double-left" />
                  <FontAwesomeIcon icon="image" />
                </span>
              </Button>
              <Button variant='outline-primary' className='me-2 mb-1 func-btn' onClick={() => this.handleCreateSnapshot('fitLeft50')}>
                <span className='sendFrameText ' title='50% balra igazítva'>
                  50% <FontAwesomeIcon icon="angle-left" />
                  <FontAwesomeIcon icon="image" />
                </span>
              </Button>
              <Button variant='outline-primary' className='me-2 mb-1 func-btn' onClick={() => this.handleCreateSnapshot('fit')}>
                <span className='sendFrameText ' title='Középre igazítva '>
                  <FontAwesomeIcon icon="image" />
                </span>
              </Button>
              <Button variant='outline-primary' className='me-2 mb-1 func-btn' onClick={() => this.handleCreateSnapshot('fitRight50')}>
                <span className='sendFrameText ' title='50% jobbra igazítva'>
                  <FontAwesomeIcon icon="image" />
                  <FontAwesomeIcon icon="angle-right" />
                  50%
                </span>
              </Button>
              <Button variant='outline-primary' className='me-2 mb-1 func-btn' onClick={() => this.handleCreateSnapshot('fitRight100')}>
                <span className='sendFrameText ' title='Teljesen jobbra igazítva'>
                  <FontAwesomeIcon icon="image" />
                  <FontAwesomeIcon icon="angle-double-right" />
                </span>
              </Button>
              <hr />
              <Button variant='outline-primary' className='me-2 mb-1 func-btn' onClick={() => this.handleCreateSnapshot('fitMin10')}>
                <span className='sendFrameText ' title='kicsit balra'>
                  <FontAwesomeIcon icon="caret-left" />
                  <FontAwesomeIcon icon="image" />
                </span>
              </Button>
              <Button variant='outline-primary' className='me-2 mb-1 func-btn' onClick={() => this.handleCreateSnapshot('fitPlus10')}>
                <span className='sendFrameText' title='kicsit jobbra'>
                  <FontAwesomeIcon icon="image" />
                  <FontAwesomeIcon icon="caret-right" />
                </span>
              </Button>
              <Button variant='outline-primary' className='me-2 mb-1 func-btn' onClick={() => this.handleCreateSnapshot('scale')}>
                <span className='sendFrameText '>
                  <FontAwesomeIcon icon="expand" />
                  TELJES KÉP
                </span>
              </Button>
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col xs={12}>
            <span className='crop-input-title'>KÉP ELNEVEZÉSE*(kötelező):</span>
            <input placeholder={'Add meg a kép nevét'} type='text' className='form-control crop-input-filename' id='snapshotNameFromInput' name='snapshotNameFromInput' autoComplete='off' required onChange={this.setAndValidateSnapshotName} />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {snapInputName && !snapInputError && (
              <div className='success-info-filename'>
                <FontAwesomeIcon icon="check-circle" /> {_SETTINGS?.snap.ok} <b>{`${snapInputName}.${_SETTINGS.basic.imgFormat}`}</b>
              </div>
            )}
            {snapInputError && (
              <div className='warning-info-filename'>
                <FontAwesomeIcon icon="exclamation-circle" /> {snapInputErrorMsg}
              </div>
            )}
          </Col>
        </Row>

        <Row>
          <Col>
            <Tabs defaultActiveKey='mtva' id='imgTab' className={`${snapTrgPos === 'scale' ? 'mt-5 mb-3 photo-functions' : 'dispNone'}`}>
              {snapshotDesignBg && outputRes
                ? snapshotDesignBg?.map((design: any, key: number) => {
                  return (
                    <Tab key={design.safeName + key} className={'imgTabWidth'} eventKey={design.safeName} title={snapTrgPos === 'scale' ? <span className='tab-text-func'>{design.name}</span> : <span className='dispNone' />}>
                      {/*TODO:: remove
                          Ezt a részt jó lenne refaktorálni és kiszervezni egy class-metódusba.
                          A generált képeket egy rácsra is rá lehetne tenni, hogy lehessen látni a kis képek pozíconálását módosítás közben. 
                          pl.: Amikor a user jobbra - balra léptet látnia kell, több képet is egyszerre
                        */}
                      {outputRes?.map((res: any, kk: number) => {
                        return (
                          <div key={`res_${kk}`}>
                            {res.data.map((r: any, k: number) => {
                              return (
                                r.opt && (
                                  <div key={`cardCont_${k}`}>
                                    <h3 className={'cropped-image-type'}>{outputRes[kk].name}</h3>
                                    <Card key={`card_${design.safeName}_${r.label}`}>
                                      <canvas key={`${outputRes[kk].name}_${design.safeName}_${r.label}`} id={`${outputRes[kk].name}_${design.safeName}_${r.label}`} width={r.width} height={r.height} />
                                      <Card.Body>
                                        <Card.Text>{r.label}</Card.Text>
                                      </Card.Body>
                                    </Card>
                                  </div>
                                )
                              );
                            })}
                          </div>
                        );
                      })}
                    </Tab>
                  );
                })
                : null}
            </Tabs>
          </Col>
        </Row>
      </>
    );
  }
}
export default PhotoPanel;
